import { useFrame, useThree } from '@react-three/fiber'
import { useRef, useEffect, useState, Suspense } from 'react'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'

// Breakpoint per mobile (standard Tailwind md)
const MOBILE_BREAKPOINT = 768

function Model({ color }: { color: string }) {
  const { scene } = useGLTF('/3D/daft.glb')
  
  // Clone the scene to modify materials
  const clonedScene = scene.clone()
  
  // Make all materials chrome-like with custom color
  clonedScene.traverse((child) => {
    if ((child as THREE.Mesh).isMesh) {
      (child as THREE.Mesh).material = new THREE.MeshStandardMaterial({
        color: color,
        metalness: 1.0,
        roughness: 0.01,
        envMapIntensity: 2.0
      })
    }
  })
  
  return (
    <primitive object={clonedScene} />
  )
}

export function Scene3D({ helmetColor = '#00ffff', helmetSize = 3.1875 }: { helmetColor?: string; helmetSize?: number }) {
  const { camera } = useThree()
  const modelRef = useRef<THREE.Group>(null)
  const mouseX = useRef(0)
  const mouseY = useRef(0)
  const mouseLightRef = useRef<THREE.PointLight>(null)
  const rotationRef = useRef(0)
  const crosshairRef = useRef<THREE.Group>(null)
  const [isMobile, setIsMobile] = useState(false)

  // Rileva se siamo su mobile
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    }
    
    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)
    return () => window.removeEventListener('resize', checkIsMobile)
  }, [])

  // Handle mouse movement - solo su desktop
  useEffect(() => {
    if (isMobile) return

    const handleMouseMove = (event: MouseEvent) => {
      mouseX.current = (event.clientX / window.innerWidth) * 2 - 1
      mouseY.current = -(event.clientY / window.innerHeight) * 2 + 1
    }

    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [isMobile])

  useFrame((state, delta) => {
    if (modelRef.current) {
      if (isMobile) {
        // Su mobile: rotazione automatica lenta
        modelRef.current.rotation.y += delta * 0.15
        rotationRef.current = modelRef.current.rotation.y
      } else {
        // Su desktop: ruota in base alla posizione del mouse
        const targetRotationY = mouseX.current * Math.PI * 0.5
        modelRef.current.rotation.y += (targetRotationY - modelRef.current.rotation.y) * 0.1
        rotationRef.current = modelRef.current.rotation.y
      }
    }

    if (mouseLightRef.current) {
      const x = mouseX.current * 5
      const y = mouseY.current * 5
      mouseLightRef.current.position.set(x, y, 3)
    }

    if (crosshairRef.current && !isMobile) {
      const rect = state.gl.domElement.getBoundingClientRect()
      const x = mouseX.current * (rect.width / 2)
      const y = mouseY.current * (rect.height / 2)
      crosshairRef.current.position.set(x, y, 0)
    }
  })

  return (
    <>
      {/* Rotating Colored PointLights */}
      <pointLight 
        position={[Math.sin(rotationRef.current) * 3, 1, Math.cos(rotationRef.current) * 3]} 
        intensity={1000.0} 
        color="#00ffff" 
        distance={50} 
      />
      <pointLight 
        position={[Math.cos(rotationRef.current) * 3, -1, Math.sin(rotationRef.current) * 3]} 
        intensity={1000.0} 
        color="#ff00ff" 
        distance={50} 
      />
      <pointLight 
        position={[Math.sin(rotationRef.current + Math.PI) * 2, 0, Math.cos(rotationRef.current + Math.PI) * 2]} 
        intensity={800.0} 
        color="#00ff00" 
        distance={40} 
      />
      <pointLight 
        position={[Math.cos(rotationRef.current + Math.PI) * 2, 0, Math.sin(rotationRef.current + Math.PI) * 2]} 
        intensity={800.0} 
        color="#ff0000" 
        distance={40} 
      />
      
      {/* Fixed Neon Lights */}
      <pointLight position={[1, 0.5, 1]} intensity={1000.0} color="#ff0055" distance={50} />
      <pointLight position={[-1, 0.5, -1]} intensity={1000.0} color="#00ffff" distance={50} />
      <pointLight position={[0, 1, 1]} intensity={800.0} color="#ff0055" distance={40} />
      <pointLight position={[0, -1, -1]} intensity={800.0} color="#00ffff" distance={40} />
      <pointLight position={[1, 0, 0]} intensity={120.0} color="#ffffff" distance={40} />
      <pointLight position={[-1, 0, 0]} intensity={600.0} color="#ffff00" distance={40} />
      <pointLight position={[0, 0, 2]} intensity={1200.0} color="#ff00ff" distance={60} />
      <pointLight position={[2, 0, 0]} intensity={1200.0} color="#00ff00" distance={60} />
      
      {/* Mouse-controlled Light */}
      <pointLight 
        ref={mouseLightRef}
        position={[0, 0, 3]} 
        intensity={4000.0} 
        color="#00ffff" 
        distance={60}
      />
      
      {/* Crosshair - Hidden on mobile */}
      <group ref={crosshairRef} position={[0, 0, 0]} visible={!isMobile}>
        <mesh>
          <ringGeometry args={[0.02, 0.025, 32]} />
          <meshBasicMaterial color="#888888" transparent opacity={0.8} />
        </mesh>
        <line>
          <bufferGeometry>
            <bufferAttribute attach="attributes-position" array={new Float32Array([-1, 0, 0, 1, 0, 0])} itemSize={3} count={2} />
          </bufferGeometry>
          <lineBasicMaterial color="#888888" />
        </line>
        <line>
          <bufferGeometry>
            <bufferAttribute attach="attributes-position" array={new Float32Array([0, -1, 0, 0, 1, 0])} itemSize={3} count={2} />
          </bufferGeometry>
          <lineBasicMaterial color="#888888" />
        </line>
      </group>
      
      {/* Daft Punk Model */}
      <group ref={modelRef} position={[0, 0, 0]} scale={[helmetSize * 0.75, helmetSize * 0.75, helmetSize * 0.75]}>
        <Model color={helmetColor} />
      </group>
    </>
  )
}
